import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Key } from 'react';
import type { SortDescriptor } from '@react-types/shared';
import { ActionButton, StatusLight, Text } from "@react-spectrum/s2";
import { Add } from '../icons/s2';
import { toast } from '../../utils/toast';
import { CCOotbModelConfiguration, ModelProvider, ModelRegistryType } from '../../types';
import { fetchOotbModelConfigurations, deleteOotbModelConfiguration, fetchSystemDefaults } from '../../services/api';
import { utilities } from '../Models/modelUtilities';
import type { SortOptions } from '../../services/api/paginationParams';
import { ColumnDefinition } from '../common/DataTable';
import { SourceBadge } from '../common/SourceBadge';
import { FilterableTable, FiltersState } from '../common/FilterableTable';
import { useProviderMetadata } from '../../hooks/useProviderMetadata';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { ConfigureModelDialog } from './ConfigureModelDialog';
import { modelTypeFilterOptions, yesNoFilterOptions, getModelValueLabel } from '../../constants/modelOptions';
import { ThirdPartyModelDisclaimer } from '../common/ThirdPartyModelDisclaimer';
import { TablePanel } from '../common/TablePanel';
import '../Models/modelsTableScroll.css';

// FE column keys are camelCase; the wire/DB columns are snake_case for these.
const SORT_COLUMN_WIRE_NAMES: Record<string, string> = {
  name: 'configuration_name',
  isValid: 'is_valid',
  isDeprecated: 'is_deprecated',
};

export const ConfiguredModelsTable: React.FC = () => {
  const { t } = useTranslation();
  const { providers } = useProviderMetadata();
  const [configurations, setConfigurations] = useState<CCOotbModelConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterValues, setFilterValues] = useState<FiltersState>({});
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>();
  // config_id -> utility labels this config is the system default for.
  const [defaultForByConfig, setDefaultForByConfig] = useState<Record<string, string[]>>({});
  const [editingConfiguration, setEditingConfiguration] = useState<CCOotbModelConfiguration | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  // Latest-wins guard for overlapping filter/sort-driven refetches.
  const loadSeqRef = useRef(0);

  // provider/type/isValid/isDeprecated are exact-match selects and go
  // server-side (plus sort); name/model are free-text substring searches the
  // backend can't equality-match, so they stay client-side — same split as
  // the Model Registry table.
  const serverFilters = useMemo(
    () => ({
      provider: (filterValues.provider as ModelProvider) || undefined,
      type: (filterValues.type as ModelRegistryType) || undefined,
      isValid: filterValues.isValid ? filterValues.isValid === 'true' : undefined,
      isDeprecated: filterValues.isDeprecated ? filterValues.isDeprecated === 'true' : undefined,
    }),
    [filterValues.provider, filterValues.type, filterValues.isValid, filterValues.isDeprecated],
  );

  const serverSort = useMemo<SortOptions | undefined>(() => {
    if (!sortDescriptor?.column) return undefined;
    const column = String(sortDescriptor.column);
    return {
      column: SORT_COLUMN_WIRE_NAMES[column] ?? column,
      direction: sortDescriptor.direction === 'descending' ? 'descending' : 'ascending',
    };
  }, [sortDescriptor]);

  const loadConfigurations = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const [items, systemDefaults] = await Promise.all([
        fetchOotbModelConfigurations(serverFilters, serverSort),
        fetchSystemDefaults().catch(() => []),
      ]);
      if (seq !== loadSeqRef.current) return; // superseded by a newer load
      setConfigurations(items);
      const utilityLabel = (name: string) => utilities(t).find((u) => u.key === name)?.label ?? name;
      const byConfig: Record<string, string[]> = {};
      systemDefaults.forEach((entry) => {
        if (entry.configurationId) {
          (byConfig[entry.configurationId] ??= []).push(utilityLabel(entry.utility));
        }
      });
      setDefaultForByConfig(byConfig);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      toast.negative(err instanceof Error ? err.message : t('commandCenter.configuredModels.failedToLoad'));
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [serverFilters, serverSort, t]);

  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  const filteredConfigurations = useMemo(() => {
    const nameFilter = (filterValues.name || '').toLowerCase();
    const modelFilter = (filterValues.model || '').toLowerCase();
    return configurations.filter((configuration) => {
      if (nameFilter && !configuration.name.toLowerCase().includes(nameFilter)) return false;
      if (modelFilter && !configuration.model.toLowerCase().includes(modelFilter)) return false;
      return true;
    });
  }, [configurations, filterValues.name, filterValues.model]);

  const handleAction = useCallback((key: Key, configuration: CCOotbModelConfiguration) => {
    if (key === 'edit') {
      setEditingConfiguration(configuration);
      setIsDialogOpen(true);
    } else if (key === 'delete') {
      setPendingDeleteId(configuration.id);
    }
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteId) return;
    const deleted = configurations.find((c) => c.id === pendingDeleteId);
    try {
      await deleteOotbModelConfiguration(pendingDeleteId);
      await loadConfigurations();
      toast.positive(t('commandCenter.configuredModels.deleted', { name: deleted?.name }));
    } catch (err) {
      // e.g. 409 when the configuration is still referenced — surface it
      // instead of silently leaving the row in place with no feedback.
      toast.negative(err instanceof Error ? err.message : t('commandCenter.configuredModels.failedToDelete'));
    } finally {
      setPendingDeleteId(null);
    }
  }, [pendingDeleteId, configurations, loadConfigurations, t]);

  const handleDialogSaved = useCallback(async () => {
    setIsDialogOpen(false);
    setEditingConfiguration(null);
    await loadConfigurations();
  }, [loadConfigurations]);

  const columns: ColumnDefinition<CCOotbModelConfiguration>[] = useMemo(
    () => [
      { key: 'name', label: t('commandCenter.configuredModels.columns.configurationName'), minWidth: 180, isRowHeader: true, allowsSorting: true, allowsResizing: true },
      {
        key: 'provider',
        label: t('commandCenter.configuredModels.columns.provider'),
        minWidth: 140,
        allowsSorting: true,
        allowsResizing: true,
        render: (configuration) => {
          const label = providers.find((p) => p.key === configuration.provider)?.label ?? configuration.provider;
          return <Text>{label}</Text>;
        },
      },
      { key: 'model', label: t('commandCenter.configuredModels.columns.model'), minWidth: 160, allowsSorting: true, allowsResizing: true },
      {
        key: 'type',
        label: t('commandCenter.configuredModels.columns.type'),
        minWidth: 120,
        allowsSorting: true,
        render: (configuration) => <Text>{getModelValueLabel(configuration.type, t)}</Text>,
      },
      {
        // Matches OrgModelsTable's "Default for" column: indigo bold chips (these
        // are all OOTB configs, so the org table's source-keyed variant is
        // always indigo here), flex-wrapped, "—" when this config is no
        // utility's system default.
        key: 'defaultFor',
        label: t('commandCenter.configuredModels.columns.defaultFor'),
        minWidth: 220,
        allowsResizing: true,
        render: (configuration) => {
          const utils = defaultForByConfig[configuration.id];
          if (!utils || utils.length === 0) {
            return <Text UNSAFE_style={{ color: 'var(--spectrum-global-color-gray-500)' }}>—</Text>;
          }
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {utils.map((label) => <SourceBadge key={label} variant="indigo-bold">{label}</SourceBadge>)}
            </div>
          );
        },
      },
      {
        // No `align: 'center'` — StatusLight's dot+gap chrome renders wider
        // than the plain header label, so centering each independently
        // (how Spectrum's Column/Cell alignment works) left-shifts the dot
        // relative to the header text; `start` keeps both flush left.
        key: 'isValid',
        label: t('commandCenter.configuredModels.columns.connected'),
        minWidth: 120,
        allowsSorting: true,
        render: (configuration) => (
          <StatusLight variant={configuration.isValid ? 'positive' : 'negative'}>
            {configuration.isValid ? t('commandCenter.configuredModels.connectedYes') : t('commandCenter.configuredModels.connectedNo')}
          </StatusLight>
        ),
      },
      {
        key: 'isDeprecated',
        label: t('commandCenter.configuredModels.columns.deprecated'),
        minWidth: 140,
        allowsSorting: true,
        render: (configuration) =>
          configuration.isDeprecated ? (
            <StatusLight variant="negative">{t('commandCenter.configuredModels.statusDeprecated')}</StatusLight>
          ) : (
            <Text UNSAFE_style={{ color: 'var(--spectrum-global-color-gray-500)' }}>—</Text>
          ),
      },
    ],
    [providers, defaultForByConfig, t],
  );

  // Provider options are derived from live data (metadata), never a
  // hardcoded list — Type is a genuine fixed 2-value enum, so that one's a
  // static dropdown. Model stays free text: the registry can grow large
  // enough that a dropdown of every model name stops being usable.
  const filterConfigs = useMemo(() => {
    const providerOptions = providers.map((p) => ({ value: p.key, label: p.label }));
    return [
      { key: 'name', label: t('commandCenter.configuredModels.filters.configurationName'), placeholder: t('commandCenter.configuredModels.searchByName') },
      { key: 'provider', label: t('commandCenter.configuredModels.filters.provider'), type: 'select' as const, options: providerOptions },
      { key: 'model', label: t('commandCenter.configuredModels.filters.model'), placeholder: t('commandCenter.configuredModels.searchByModel') },
      { key: 'type', label: t('commandCenter.configuredModels.filters.type'), type: 'select' as const, options: modelTypeFilterOptions(t) },
      { key: 'isValid', label: t('commandCenter.configuredModels.filters.connected'), type: 'select' as const, options: yesNoFilterOptions(t) },
      { key: 'isDeprecated', label: t('commandCenter.configuredModels.filters.deprecated'), type: 'select' as const, options: yesNoFilterOptions(t) },
    ];
  }, [providers, t]);

  return (
    <TablePanel className="models-table-stack">
      <FilterableTable<CCOotbModelConfiguration>
        filterConfigs={filterConfigs}
        filterValues={filterValues}
        onFiltersChange={setFilterValues}
        onFiltersClear={() => setFilterValues({})}
        toolbarEnd={(
          <ActionButton
            onPress={() => {
              setEditingConfiguration(null);
              setIsDialogOpen(true);
            }}
          >
            <Add />
            <Text>{t('commandCenter.configuredModels.addModel')}</Text>
          </ActionButton>
        )}
        items={filteredConfigurations}
        columns={columns}
        getRowKey={(configuration) => configuration.id}
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}
        loading={loading}
        ariaLabel={t('commandCenter.configuredModels.ariaLabel')}
        onAction={handleAction}
        actionMenuItems={[{ key: 'edit', label: t('primitives.edit') }, { key: 'delete', label: t('primitives.delete') }]}
        emptyStateTitle={t('commandCenter.configuredModels.emptyTitle')}
        emptyStateDescription={t('commandCenter.configuredModels.emptyDescription')}
        layout="fill"
        configurableColumns
      />

      <ConfigureModelDialog
        isOpen={isDialogOpen}
        editingConfiguration={editingConfiguration}
        onOpenChange={setIsDialogOpen}
        onSaved={handleDialogSaved}
      />

      <ConfirmDialog
        isOpen={!!pendingDeleteId}
        onOpenChange={(open) => !open && setPendingDeleteId(null)}
        title={t('commandCenter.configuredModels.deleteTitle')}
        confirmLabel={t('primitives.delete')}
        onConfirm={handleConfirmDelete}
      >
        {t('commandCenter.configuredModels.deleteBody')}
      </ConfirmDialog>

      <ThirdPartyModelDisclaimer />
    </TablePanel>
  );
};
